import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API_BASE } from "../config/api";
import "../styles/AdminWhatsAppInbox.css";

// Modular Reusable Workspace Components
import InboxToolbar from "../components/inbox/InboxToolbar";
import ConversationList from "../components/inbox/ConversationList";
import ConversationHeader from "../components/inbox/ConversationHeader";
import MessageTimeline from "../components/inbox/MessageTimeline";
import MessageComposer from "../components/inbox/MessageComposer";
import CustomerIntelligencePanel from "../components/inbox/CustomerIntelligencePanel";
import TemplateModal from "../components/inbox/TemplateModal";
import NewChatModal from "../components/inbox/NewChatModal";

export default function AdminWhatsAppInbox() {
  // Core Data States
  const [conversations, setConversations] = useState(() => {
    try {
      const cached = sessionStorage.getItem("v_wa_cached_conversations");
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });

  const [selectedPhone, setSelectedPhone] = useState(() => {
    try {
      return localStorage.getItem("v_wa_active_phone") || null;
    } catch {
      return null;
    }
  });

  const [activeChat, setActiveChat] = useState(() => {
    try {
      const phone = localStorage.getItem("v_wa_active_phone");
      if (phone) {
        const cached = sessionStorage.getItem(`v_wa_cached_chat_${phone}`);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed && Array.isArray(parsed.messages)) {
            return parsed;
          }
        }
      }
    } catch {}
    return { messages: [], subscriber: null };
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [replyText, setReplyText] = useState("");
  const [inChatSearch, setInChatSearch] = useState("");
  const [showInChatSearch, setShowInChatSearch] = useState(false);

  // Operational States
  const [conversationsLoading, setConversationsLoading] = useState(() => {
    try {
      const cached = sessionStorage.getItem("v_wa_cached_conversations");
      return !cached;
    } catch {
      return true;
    }
  });
  const [chatLoading, setChatLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const loading = conversationsLoading || chatLoading;
  const [hasLoadedMap, setHasLoadedMap] = useState(() => {
    try {
      const phone = localStorage.getItem("v_wa_active_phone");
      if (phone) {
        const cached = sessionStorage.getItem(`v_wa_cached_chat_${phone}`);
        if (cached) return { [phone]: true };
      }
    } catch {}
    return {};
  });

  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [metaConnected, setMetaConnected] = useState(true);
  const [providerLabel, setProviderLabel] = useState("Interakt Connected");
  const [showContactInfo, setShowContactInfo] = useState(true);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [newChatPhone, setNewChatPhone] = useState("");
  const [newChatName, setNewChatName] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("vaithikasri_expiry_reminder");
  const [toast, setToast] = useState(null);
  const [replyToMsg, setReplyToMsg] = useState(null);

  // References for Safe DOM Scrolling
  const messagesCanvasRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const lastMsgCountRef = useRef(0);
  const lastPhoneRef = useRef(null);
  const hasAutoSelectedRef = useRef(false);

  // Lock body & root viewport while WhatsApp Inbox is active to eliminate all outer page scrollbars
  useEffect(() => {
    document.body.classList.add("wa-inbox-page");
    document.documentElement.classList.add("wa-inbox-page");
    return () => {
      document.body.classList.remove("wa-inbox-page");
      document.documentElement.classList.remove("wa-inbox-page");
    };
  }, []);

  // Toast Notification Helper
  const showToast = (text, type = "success") => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Safe inner timeline scroll - NEVER moves the outer browser window
  const scrollToBottom = useCallback((smooth = true) => {
    if (messagesCanvasRef.current) {
      messagesCanvasRef.current.scrollTo({
        top: messagesCanvasRef.current.scrollHeight,
        behavior: smooth ? "smooth" : "auto",
      });
    }
  }, []);

  // Fetch Meta/Interakt API Health Status
  const fetchMetaStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}whatsapp/status`);
      if (res.data?.status === "success" || res.data?.connected) {
        setMetaConnected(true);
        const prov = String(res.data?.provider || "").toLowerCase();
        if (prov.includes("interakt")) {
          setProviderLabel("Interakt Connected");
        } else if (prov.includes("meta")) {
          setProviderLabel("Cloud API Connected");
        } else {
          setProviderLabel(res.data?.provider || "API Connected");
        }
      } else {
        setMetaConnected(false);
        setProviderLabel("API Disconnected");
      }
    } catch {
      setMetaConnected(false);
      setProviderLabel("API Disconnected");
    }
  }, []);

  // Load Message History for Selected Contact
  const loadChat = useCallback(async (phone, silent = false) => {
    if (!phone) return;
    setSelectedPhone(phone);
    try {
      localStorage.setItem("v_wa_active_phone", phone);
    } catch {}
    setInChatSearch("");
    setShowInChatSearch(false);
    setShowContactInfo(true);

    // 1. Try to load cached messages instantly if available
    let hasCached = false;
    try {
      const cached = sessionStorage.getItem(`v_wa_cached_chat_${phone}`);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && Array.isArray(parsed.messages)) {
          setActiveChat(parsed);
          hasCached = true;
          setHasLoadedMap((prev) => ({ ...prev, [phone]: true }));
        }
      }
    } catch {}

    // 2. Only show loading indicator if we don't have cached data and not silent
    if (!hasCached && !silent) {
      setChatLoading(true);
    }

    try {
      const res = await axios.get(`${API_BASE}whatsapp/messages/${phone}`);
      if (res.data?.status === "success") {
        const chatData = {
          messages: res.data.messages || [],
          subscriber: res.data.subscriber || null,
        };
        setActiveChat(chatData);
        setHasLoadedMap((prev) => ({ ...prev, [phone]: true }));
        try {
          sessionStorage.setItem(`v_wa_cached_chat_${phone}`, JSON.stringify(chatData));
        } catch {}
      }
    } catch (err) {
      console.error("[WhatsApp Inbox] Failed to load messages:", err);
      if (!silent) showToast("Failed to load chat history", "error");
    } finally {
      setChatLoading(false);
      setHasLoadedMap((prev) => ({ ...prev, [phone]: true }));
    }
  }, []);

  // Fetch All Conversations
  const fetchConversations = useCallback(async (isManual = false) => {
    if (isManual) setRefreshing(true);
    try {
      const res = await axios.get(`${API_BASE}whatsapp/conversations`);
      if (res.data?.status === "success") {
        const list = res.data.conversations || [];
        setConversations(list);
        try {
          sessionStorage.setItem("v_wa_cached_conversations", JSON.stringify(list));
        } catch {}

        // Auto select on initial load if not selected or if remembered in localStorage
        if (!hasAutoSelectedRef.current && list.length > 0 && window.innerWidth > 820) {
          hasAutoSelectedRef.current = true;
          let target = null;
          try {
            const remembered = localStorage.getItem("v_wa_active_phone");
            if (remembered && list.some((c) => c.phone === remembered)) {
              target = remembered;
            }
          } catch {}
          if (!target) target = list[0].phone;

          // Only call loadChat if target is different from what's already being handled
          if (!selectedPhone || selectedPhone !== target) {
            loadChat(target);
          }
        }
      }
    } catch (err) {
      console.error("[WhatsApp Inbox] Failed to fetch conversations:", err);
      if (isManual) showToast("Failed to refresh conversations", "error");
    } finally {
      setConversationsLoading(false);
      if (isManual) setRefreshing(false);
    }
  }, [loadChat, selectedPhone]);

  // Initial Load + Health Check + Polling
  useEffect(() => {
    fetchMetaStatus();
    fetchConversations();
    if (selectedPhone) {
      // Revalidate active chat immediately in background on page load
      loadChat(selectedPhone, true);
    }
    const interval = setInterval(() => {
      fetchConversations();
    }, 7000);
    return () => clearInterval(interval);
  }, [fetchMetaStatus, fetchConversations, selectedPhone, loadChat]);

  // Selective Polling for Active Chat - Only updates if messages actually change
  useEffect(() => {
    if (!selectedPhone) return;
    const interval = setInterval(() => {
      axios
        .get(`${API_BASE}whatsapp/messages/${selectedPhone}`)
        .then((res) => {
          if (res.data?.status === "success") {
            const incoming = res.data.messages || [];
            setActiveChat((prev) => {
              const prevMsgs = prev.messages || [];
              const hasChanged =
                prevMsgs.length !== incoming.length ||
                incoming.some((inc, idx) => {
                  const p = prevMsgs[idx];
                  return (
                    !p ||
                    p.id !== inc.id ||
                    p.media_url !== inc.media_url ||
                    p.media_data !== inc.media_data ||
                    p.status !== inc.status ||
                    p.message_body !== inc.message_body ||
                    p.is_pinned !== inc.is_pinned ||
                    p.is_starred !== inc.is_starred
                  );
                });
              if (!hasChanged) {
                return prev;
              }
              const updated = {
                messages: incoming,
                subscriber: res.data.subscriber || prev.subscriber || null,
              };
              try {
                sessionStorage.setItem(`v_wa_cached_chat_${selectedPhone}`, JSON.stringify(updated));
              } catch {}
              return updated;
            });
            setHasLoadedMap((prev) => ({ ...prev, [selectedPhone]: true }));
          }
        })
        .catch(() => {});
    }, 4500);
    return () => clearInterval(interval);
  }, [selectedPhone]);

  // Scroll Timeline smoothly ONLY on genuine message changes
  useEffect(() => {
    const currentCount = activeChat.messages ? activeChat.messages.length : 0;
    if (selectedPhone !== lastPhoneRef.current || currentCount > lastMsgCountRef.current) {
      const isSwitching = selectedPhone !== lastPhoneRef.current;
      setTimeout(() => {
        scrollToBottom(!isSwitching);
      }, 50);
      lastMsgCountRef.current = currentCount;
      lastPhoneRef.current = selectedPhone;
    }
  }, [activeChat.messages, selectedPhone, scrollToBottom]);

  // Compute 24-Hour Service Session Window
  const computeSessionWindow = () => {
    if (!activeChat.messages || activeChat.messages.length === 0) {
      return { active: false, hoursLeft: 0 };
    }
    const inboundMsgs = [...activeChat.messages].filter((m) => m.direction === "inbound");
    if (inboundMsgs.length === 0) return { active: false, hoursLeft: 0 };

    const latestInbound = inboundMsgs[inboundMsgs.length - 1];
    const diffMs = Date.now() - new Date(latestInbound.created_at).getTime();
    const total24hMs = 24 * 60 * 60 * 1000;

    if (diffMs < total24hMs) {
      const hoursLeft = Math.max(1, Math.floor((total24hMs - diffMs) / (1000 * 60 * 60)));
      return { active: true, hoursLeft };
    }
    return { active: false, hoursLeft: 0 };
  };

  const sessionWindow = computeSessionWindow();

  // Handle Free-Form Reply Send (supporting contextual quotes)
  const handleSendReply = async (e) => {
    e?.preventDefault();
    if (!replyText.trim() || !selectedPhone || sending) return;

    const messageToSend = replyText.trim();
    const quotedContext = replyToMsg;
    setReplyText("");
    setReplyToMsg(null);
    setSending(true);

    // Optimistic UI Append
    const tempMsg = {
      id: Date.now(),
      phone: selectedPhone,
      direction: "outbound",
      message_body: messageToSend,
      message_type: "text",
      created_at: new Date().toISOString(),
      status: "sending...",
      reply_to_body: quotedContext?.message_body || null,
      reply_to_sender: quotedContext ? (quotedContext.direction === "outbound" ? "You" : quotedContext.sender_name || "Customer") : null,
      reply_to_wamid: quotedContext?.wamid || null,
    };
    setActiveChat((prev) => ({
      ...prev,
      messages: [...prev.messages, tempMsg],
    }));

    try {
      const payload = {
        phone: selectedPhone,
        message: messageToSend,
      };
      if (quotedContext) {
        payload.reply_to_wamid = quotedContext.wamid || null;
        payload.reply_to_body = quotedContext.message_body || null;
        payload.reply_to_sender = quotedContext.direction === "outbound" ? "You" : quotedContext.sender_name || "Customer";
      }

      const res = await axios.post(`${API_BASE}whatsapp/reply`, payload);

      if (res.data?.status === "success") {
        const refresh = await axios.get(`${API_BASE}whatsapp/messages/${selectedPhone}`);
        if (refresh.data?.status === "success") {
          const chatData = {
            messages: refresh.data.messages || [],
            subscriber: refresh.data.subscriber || null,
          };
          setActiveChat(chatData);
          setHasLoadedMap((prev) => ({ ...prev, [selectedPhone]: true }));
          try {
            sessionStorage.setItem(`v_wa_cached_chat_${selectedPhone}`, JSON.stringify(chatData));
          } catch {}
        }
        fetchConversations();
        showToast(`Message sent successfully via ${providerLabel ? providerLabel.replace(" Connected", "") : "WhatsApp"}!`);
      }
    } catch (err) {
      const errMsg =
        err.response?.data?.message ||
        err.response?.data?.detail?.error?.message ||
        err.message;
      showToast("Failed to send message: " + errMsg, "error");
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  // Reply Quoted Initiator
  const handleInitiateReply = (msg) => {
    setReplyToMsg(msg);
    inputRef.current?.focus();
  };

  const handleCancelReply = () => {
    setReplyToMsg(null);
  };

  // Emoji React Handler (Syncs with Meta Cloud API)
  const handleReact = async (msg, emoji) => {
    try {
      const res = await axios.post(`${API_BASE}whatsapp/messages/${msg.id}/react`, { emoji });
      if (res.data?.status === "success") {
        const updatedReactions = res.data.reactions || [];
        setActiveChat((prev) => {
          const updated = {
            ...prev,
            messages: (prev.messages || []).map((m) =>
              m.id === msg.id ? { ...m, reactions: updatedReactions } : m
            ),
          };
          try {
            sessionStorage.setItem(`v_wa_cached_chat_${selectedPhone}`, JSON.stringify(updated));
          } catch {}
          return updated;
        });
      }
    } catch (err) {
      showToast("Failed to react: " + (err.response?.data?.message || err.message), "error");
    }
  };

  // Star / Unstar Message Handler
  const handleToggleStar = async (msg) => {
    try {
      const res = await axios.post(`${API_BASE}whatsapp/messages/${msg.id}/star`);
      if (res.data?.status === "success") {
        const nextState = res.data.is_starred;
        setActiveChat((prev) => {
          const updated = {
            ...prev,
            messages: (prev.messages || []).map((m) =>
              m.id === msg.id ? { ...m, is_starred: nextState } : m
            ),
          };
          try {
            sessionStorage.setItem(`v_wa_cached_chat_${selectedPhone}`, JSON.stringify(updated));
          } catch {}
          return updated;
        });
        showToast(nextState ? "Message starred ⭐" : "Message unstarred");
      }
    } catch (err) {
      showToast("Failed to star: " + (err.response?.data?.message || err.message), "error");
    }
  };

  // Pin / Unpin Message Handler
  const handleTogglePin = async (msg) => {
    try {
      const res = await axios.post(`${API_BASE}whatsapp/messages/${msg.id}/pin`);
      if (res.data?.status === "success") {
        const nextState = res.data.is_pinned;
        setActiveChat((prev) => {
          const updated = {
            ...prev,
            messages: (prev.messages || []).map((m) =>
              m.id === msg.id
                ? { ...m, is_pinned: nextState }
                : nextState
                ? { ...m, is_pinned: false }
                : m
            ),
          };
          try {
            sessionStorage.setItem(`v_wa_cached_chat_${selectedPhone}`, JSON.stringify(updated));
          } catch {}
          return updated;
        });
        showToast(nextState ? "Message pinned 📌" : "Message unpinned");
      }
    } catch (err) {
      showToast("Failed to pin: " + (err.response?.data?.message || err.message), "error");
    }
  };

  // Copy Message Text Helper
  const handleCopyText = (text) => {
    if (!text) return;
    try {
      navigator.clipboard?.writeText(text);
      showToast("Message copied to clipboard");
    } catch {
      showToast("Copied");
    }
  };

  // Handle Meta Approved Template Send
  const handleSendApprovedTemplate = async () => {
    if (!selectedPhone) return;
    setSending(true);
    try {
      const sub = activeChat.subscriber || {};
      const subName = sub.user_name || sub.user_full_name || "Subscriber";
      const subId = sub.receipt_number || (sub.id ? `VS-${sub.id}` : "N/A");
      const validMonth = sub.end_date || "1 Year";

      let bodyValues = [subName, subId, validMonth];
      if (selectedTemplate === "vaithikasri_new_subscription" || selectedTemplate === "vaithikasri_renewal_success") {
        bodyValues = [subName, subId, sub.package_name || "Annual Subscription", validMonth];
      } else if (selectedTemplate === "vaithikasri_monthly_dispatch_alert") {
        const curMonth = new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" });
        bodyValues = [curMonth, "தபால் மூலம்"];
      }

      const res = await axios.post(`${API_BASE}whatsapp/send_template`, {
        phone: selectedPhone,
        template_name: selectedTemplate,
        body_values: bodyValues,
        user_traits: { subscriptionId: subId }
      });

      if (res.data?.status === "success") {
        showToast(`Template "${selectedTemplate}" delivered!`);
        setShowTemplateModal(false);
        const refresh = await axios.get(`${API_BASE}whatsapp/messages/${selectedPhone}`);
        if (refresh.data?.status === "success") {
          const chatData = {
            messages: refresh.data.messages || [],
            subscriber: refresh.data.subscriber || null,
          };
          setActiveChat(chatData);
          setHasLoadedMap((prev) => ({ ...prev, [selectedPhone]: true }));
          try {
            sessionStorage.setItem(`v_wa_cached_chat_${selectedPhone}`, JSON.stringify(chatData));
          } catch {}
        }
        fetchConversations();
      } else {
        const errMsg = res.data?.result?.error?.error?.message || res.data?.result?.message || "Failed to trigger template";
        showToast(errMsg, "error");
      }
    } catch (err) {
      showToast("Template error: " + (err.response?.data?.message || err.message), "error");
    } finally {
      setSending(false);
    }
  };

  // Quick Canned Suggested Reply Inserter
  const handleInsertQuickReply = (text) => {
    setReplyText(text);
    inputRef.current?.focus();
  };

  // Retry Failed Message Callback
  const handleRetryMessage = (failedMsg) => {
    if (failedMsg?.message_body) {
      if (failedMsg.message_type === "template" || failedMsg.message_body?.startsWith("[Template:")) {
        const tplMatch = failedMsg.message_body.match(/^\[Template:\s*([a-zA-Z0-9_]+)\]/);
        if (tplMatch && tplMatch[1]) {
          setSelectedTemplate(tplMatch[1]);
        }
        setShowTemplateModal(true);
        showToast("Template modal opened. Review and click Send Template to retry.", "info");
      } else {
        setReplyText(failedMsg.message_body);
        inputRef.current?.focus();
        showToast("Message restored to composer. Click Send to retry.", "info");
      }
    }
  };

  // Send Media Attachment (video, audio, image, document) via Meta Cloud API
  const handleSendMedia = async (file, caption) => {
    if (!file || !selectedPhone || sending) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append("media", file);
      formData.append("phone", selectedPhone);
      if (caption && caption.trim()) {
        formData.append("caption", caption.trim());
      }

      const res = await axios.post(`${API_BASE}whatsapp/send_media`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data?.status === "success") {
        showToast(`${res.data.type || "Media"} sent successfully!`);
        const refresh = await axios.get(`${API_BASE}whatsapp/messages/${selectedPhone}`);
        if (refresh.data?.status === "success") {
          setActiveChat({
            messages: refresh.data.messages || [],
            subscriber: refresh.data.subscriber || null,
          });
        }
        fetchConversations();
      } else {
        showToast("Failed to send media: " + (res.data?.message || "Unknown error"), "error");
      }
    } catch (err) {
      const errMsg =
        err.response?.data?.message ||
        err.message ||
        "Failed to send media attachment";
      showToast("Media send error: " + errMsg, "error");
    } finally {
      setSending(false);
    }
  };


  const handleStartNewChat = (e) => {
    e.preventDefault();
    const clean = newChatPhone.replace(/[^0-9]/g, "");
    if (!clean || clean.length < 10) {
      showToast("Please enter a valid 10-digit mobile number", "error");
      return;
    }
    const fullPhone = clean.length === 10 ? `91${clean}` : clean;
    setShowNewChatModal(false);
    setNewChatPhone("");
    setNewChatName("");
    loadChat(fullPhone);
  };

  const activeContact =
    conversations.find((c) => c.phone === selectedPhone) ||
    (activeChat.subscriber
      ? {
          phone: selectedPhone,
          name: activeChat.subscriber.user_name || activeChat.subscriber.user_full_name,
          subscription_id: activeChat.subscriber.id,
        }
      : { phone: selectedPhone });

  const subscribersCount = conversations.filter((c) => !!c.subscription_id).length;
  const unreadCount = conversations.reduce((acc, c) => acc + (c.unread_count || 0), 0);

  return (
    <div className={`v-workspace-container ${selectedPhone ? "has-active-chat" : "no-active-chat"}`}>
      {/* Toast Notification Notification */}
      {toast && (
        <div className={`v-toast-card ${toast.type === "error" ? "error" : "success"}`} role="alert">
          <i
            className={`bi ${toast.type === "error" ? "bi-exclamation-triangle-fill" : "bi-check-circle-fill"} me-2`}
          ></i>
          <span>{toast.text}</span>
          <button type="button" className="v-toast-close" onClick={() => setToast(null)}>
            ✕
          </button>
        </div>
      )}

      {/* Top Refined Workspace Toolbar */}
      <InboxToolbar
        conversationsCount={conversations.length}
        unreadCount={unreadCount}
        subscribersCount={subscribersCount}
        metaConnected={metaConnected}
        providerLabel={providerLabel}
        refreshing={refreshing}
        onRefresh={() => fetchConversations(true)}
        onNewChat={() => setShowNewChatModal(true)}
      />

      {/* 3-Column Workspace Main Stage */}
      <div className={`v-workspace-stage ${selectedPhone ? "has-active-chat" : "no-active-chat"}`}>
        {/* 1. Left Conversation Navigation */}
        <ConversationList
          conversations={conversations}
          selectedPhone={selectedPhone}
          onSelectChat={loadChat}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          filterType={filterType}
          setFilterType={setFilterType}
          loading={conversationsLoading}
          onNewChat={() => setShowNewChatModal(true)}
        />

        {/* 2. Center Conversation Canvas */}
        <main className="v-canvas-stage">
          {selectedPhone ? (
            <>
              {/* Refined Canvas Header */}
              <ConversationHeader
                activeContact={activeContact}
                selectedPhone={selectedPhone}
                sessionWindow={sessionWindow}
                showContactInfo={showContactInfo}
                onToggleContactInfo={() => setShowContactInfo(!showContactInfo)}
                onOpenTemplateModal={() => setShowTemplateModal(true)}
                onRefreshChat={() => loadChat(selectedPhone)}
                onBackToList={() => setSelectedPhone(null)}
                showInChatSearch={showInChatSearch}
                onToggleChatSearch={() => setShowInChatSearch(!showInChatSearch)}
                inChatSearch={inChatSearch}
                setInChatSearch={setInChatSearch}
              />

              {/* Message Timeline */}
              <MessageTimeline
                messages={activeChat.messages}
                loading={chatLoading}
                hasLoaded={!!hasLoadedMap[selectedPhone] || (activeChat.messages && activeChat.messages.length > 0)}
                messagesCanvasRef={messagesCanvasRef}
                messagesEndRef={messagesEndRef}
                onInsertQuickReply={handleInsertQuickReply}
                onOpenTemplateModal={() => setShowTemplateModal(true)}
                inChatSearch={inChatSearch}
                onScrollToBottom={scrollToBottom}
                onRetryMessage={handleRetryMessage}
                onInitiateReply={handleInitiateReply}
                onReact={handleReact}
                onToggleStar={handleToggleStar}
                onTogglePin={handleTogglePin}
                onCopyText={handleCopyText}
                providerLabel={providerLabel}
              />

              {/* Message Composer Surface */}
              <MessageComposer
                replyText={replyText}
                setReplyText={setReplyText}
                sending={sending}
                sessionWindow={sessionWindow}
                inputRef={inputRef}
                onSendReply={handleSendReply}
                onInsertQuickReply={handleInsertQuickReply}
                onOpenTemplateModal={() => setShowTemplateModal(true)}
                onSendMedia={handleSendMedia}
                replyToMsg={replyToMsg}
                onCancelReply={handleCancelReply}
              />

            </>
          ) : (
            /* Editorial Hero / Empty Workspace State */
            <div className="v-hero-canvas">
              <div className="v-hero-content-box">
                <div className="v-hero-seal">
                  <i className="bi bi-chat-quote-fill"></i>
                </div>
                <h2>VaithikaSri Customer Messaging Workspace</h2>
                <p>
                  Centralized multi-channel communications for readers, active magazine subscribers, and spiritual patrons. Powered directly by official Meta WhatsApp Cloud API.
                </p>

                <div className="v-hero-dossier-grid">
                  <div className="v-dossier-feat">
                    <i className="bi bi-shield-check text-gold"></i>
                    <div>
                      <strong>Direct Meta Cloud API</strong>
                      <span>Zero middleman fees • Encrypted end-to-end</span>
                    </div>
                  </div>
                  <div className="v-dossier-feat">
                    <i className="bi bi-database-check text-maroon"></i>
                    <div>
                      <strong>Live Subscriber CRM</strong>
                      <span>Connected to Neon Database • Auto address & dispatch sync</span>
                    </div>
                  </div>
                  <div className="v-dossier-feat">
                    <i className="bi bi-lightning-charge text-success"></i>
                    <div>
                      <strong>Automated Meta Templates</strong>
                      <span>Pre-approved monthly dispatch notices & renewal receipts</span>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <button
                    type="button"
                    className="v-btn-primary lg"
                    onClick={() => setShowNewChatModal(true)}
                  >
                    <i className="bi bi-plus-lg me-2"></i>
                    <span>Initiate New Conversation</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>

        {/* 3. Right Customer Intelligence Panel */}
        {selectedPhone && showContactInfo && (
          <CustomerIntelligencePanel
            activeContact={activeContact}
            selectedPhone={selectedPhone}
            subscriber={activeChat.subscriber}
            onClose={() => setShowContactInfo(false)}
            onSelectTemplateAndOpen={(tpl) => {
              setSelectedTemplate(tpl);
              setShowTemplateModal(true);
            }}
          />
        )}
      </div>

      {/* New Conversation Modal */}
      {showNewChatModal && (
        <NewChatModal
          newChatPhone={newChatPhone}
          setNewChatPhone={setNewChatPhone}
          newChatName={newChatName}
          setNewChatName={setNewChatName}
          onClose={() => setShowNewChatModal(false)}
          onSubmit={handleStartNewChat}
        />
      )}

      {/* Approved Meta Template Modal */}
      {showTemplateModal && (
        <TemplateModal
          selectedTemplate={selectedTemplate}
          setSelectedTemplate={setSelectedTemplate}
          selectedPhone={selectedPhone}
          activeContact={activeContact}
          sending={sending}
          onClose={() => setShowTemplateModal(false)}
          onSendApprovedTemplate={handleSendApprovedTemplate}
        />
      )}
    </div>
  );
}
